import { useState, useEffect, useRef } from 'react';
import { FaLink, FaPlus, FaTimes, FaExternalLinkAlt, FaSpinner, FaSync } from 'react-icons/fa';
import { useBaseComponentFullscreen } from './BaseComponent';
import { isValidUrl, fetchLinkPreview } from '../../services/linkPreview';

export default function LinkListComponent({ component, onUpdate }) {
  const isFullscreen = useBaseComponentFullscreen();
  
  // Ensure all links have IDs - migration helper
  const ensureLinksHaveIds = (data) => {
    if (!data || !data.links) return { links: [] };
    
    const linksWithIds = data.links.map((link, index) => {
      // Handle case where AI sends just URL strings
      if (typeof link === 'string') {
        return {
          id: Math.floor(Date.now() + index * 1000), // Unique ID
          url: link,
          added_at: new Date().toISOString(),
          preview: { loading: true } // Start with loading state for new URLs
        };
      }
      
      // Handle case where link is an object but missing ID
      if (!link.id) {
        return {
          ...link,
          id: Math.floor(Date.now() + index * 1000) // Unique ID
        };
      }
      
      return link;
    });
    
    return { ...data, links: linksWithIds };
  };
  
  const data = ensureLinksHaveIds(component.data || { links: [] });
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [loadingPreviews, setLoadingPreviews] = useState({});
  const [refreshingAll, setRefreshingAll] = useState(false);
  
  // Auto-migrate component data if IDs were missing
  useEffect(() => {
    const originalData = component.data || { links: [] };
    const migratedData = ensureLinksHaveIds(originalData);
    
    // If data was migrated (IDs were added or strings converted), update the component
    if (JSON.stringify(originalData) !== JSON.stringify(migratedData)) {
      console.log('Migrating component data - converting strings/adding missing IDs');
      onUpdate({
        ...component,
        data: migratedData
      });
      
      // Fetch previews for any newly converted string URLs
      migratedData.links.forEach(link => {
        if (link.preview && link.preview.loading) {
          // Small delay to avoid overwhelming the API
          setTimeout(() => {
            fetchLinkPreviewData(link.url, link.id);
          }, Math.random() * 1000);
        }
      });
    }
  }, []); // Run once on mount
  
  // Use a ref to track the current component state for async operations
  const componentRef = useRef(component);
  componentRef.current = component;

  // Debug logging
  console.log('LinkListComponent render - component:', component);
  console.log('LinkListComponent render - data:', data);
  console.log('LinkListComponent render - links:', data.links);

  // Track component changes
  useEffect(() => {
    console.log('Component data changed:', component.data);
    console.log('Links count:', data.links.length);
  }, [component.data, data.links]);

  const fetchLinkPreviewData = async (url, linkId) => {
    setLoadingPreviews(prev => ({ ...prev, [linkId]: true }));
    
    try {
      // Add a small delay to simulate real preview fetching like chat apps
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const preview = await fetchLinkPreview(url);
      
      console.log('Preview fetched for', url, ':', preview);
      
      // Get the current component state from the ref
      const currentComponent = componentRef.current;
      const currentData = currentComponent.data || { links: [] };
      
      console.log('Current component from ref:', currentComponent);
      console.log('Current links from ref:', currentData.links);
      
      // Make sure we find the link by ID in the current data
      const currentLink = currentData.links.find(link => link.id === linkId);
      if (!currentLink) {
        console.error('Link with id', linkId, 'not found in current data!');
        setLoadingPreviews(prev => ({ ...prev, [linkId]: false }));
        return;
      }
      
      // Update the link with preview data
      const updatedLinks = currentData.links.map(link => 
        link.id === linkId 
          ? { ...link, preview }
          : link
      );
      
      console.log('Updating links with preview:', updatedLinks);
      
      const updatedComponent = {
        ...currentComponent,
        data: {
          links: updatedLinks
        }
      };
      
      console.log('Sending updated component to onUpdate:', updatedComponent);
      
      onUpdate(updatedComponent);
    } catch (error) {
      console.error('Failed to fetch link preview:', error);
      
      // Get current state for error update
      const currentComponent = componentRef.current;
      const currentData = currentComponent.data || { links: [] };
      
      const updatedLinks = currentData.links.map(link => 
        link.id === linkId 
          ? { ...link, preview: { error: 'Failed to load preview' } }
          : link
      );

      onUpdate({
        ...currentComponent,
        data: {
          links: updatedLinks
        }
      });
    } finally {
      setLoadingPreviews(prev => ({ ...prev, [linkId]: false }));
    }
  };

  const addLink = () => {
    if (!newLinkUrl.trim()) return;
    
    if (!isValidUrl(newLinkUrl.trim())) {
      alert('Please enter a valid URL');
      return;
    }
    
    const newLink = {
      id: Math.floor(Date.now()), // Ensure it's an integer
      url: newLinkUrl.trim(),
      added_at: new Date().toISOString(),
      preview: { loading: true } // Start with loading state
    };

    const updatedLinks = [...data.links, newLink];
    
    console.log('Adding link:', newLink);
    console.log('Current links:', data.links);
    console.log('Updated links:', updatedLinks);
    
    const updatedComponent = {
      ...component,
      data: {
        links: updatedLinks
      }
    };
    
    console.log('Updated component:', updatedComponent);
    
    onUpdate(updatedComponent);

    // Fetch preview for the new link (this will update the loading state)
    fetchLinkPreviewData(newLink.url, newLink.id);
    
    setNewLinkUrl('');
  };

  const removeLink = (linkId) => {
    const updatedLinks = data.links.filter(link => link.id !== linkId);
    console.log('Removing link with id:', linkId);
    console.log('Updated links after removal:', updatedLinks);
    
    onUpdate({
      ...component,
      data: {
        links: updatedLinks
      }
    });
  };

  const refreshAllPreviews = async () => {
    if (data.links.length === 0 || refreshingAll) return;
    
    setRefreshingAll(true);
    console.log('Refreshing all link previews');
    
    try {
      // Set all links to loading state
      const linksWithLoading = data.links.map(link => ({
        ...link,
        preview: { loading: true }
      }));
      
      onUpdate({
        ...component,
        data: {
          links: linksWithLoading
        }
      });
      
      // Fetch previews for all links concurrently
      const refreshPromises = data.links.map(async (link) => {
        try {
          const preview = await fetchLinkPreview(link.url);
          return { id: link.id, preview };
        } catch (error) {
          console.error(`Failed to refresh preview for ${link.url}:`, error);
          return { id: link.id, preview: { error: 'Failed to load preview' } };
        }
      });
      
      const results = await Promise.all(refreshPromises);
      
      // Update all links with new preview data
      const currentComponent = componentRef.current;
      const currentData = currentComponent.data || { links: [] };
      
      const updatedLinks = currentData.links.map(link => {
        const result = results.find(r => r.id === link.id);
        return result ? { ...link, preview: result.preview } : link;
      });
      
      onUpdate({
        ...currentComponent,
        data: {
          links: updatedLinks
        }
      });
      
      console.log('Successfully refreshed all previews');
    } catch (error) {
      console.error('Error during refresh operation:', error);
    } finally {
      setRefreshingAll(false);
    }
  };

  return (
    <div className="h-full max-h-[28rem] flex flex-col">
      {/* Add Link Section - Always Visible */}
      <div className="mb-3">
        <div className="flex gap-2">
          <input
            type="url"
            value={newLinkUrl}
            onChange={(e) => setNewLinkUrl(e.target.value)}
            placeholder="Enter URL (e.g., https://example.com)"
            className="flex-1 px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] text-sm"
            onKeyPress={(e) => e.key === 'Enter' && addLink()}
          />
          <button
            onClick={addLink}
            className="flex items-center px-3 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 text-sm"
            disabled={!newLinkUrl.trim()}
          >
            <FaPlus className="mr-2 text-xs" />
            Add
          </button>
          {data.links.length > 0 && (
            <button
              onClick={refreshAllPreviews}
              className="flex items-center px-3 py-2 border border-[var(--border-color)] text-[var(--text-secondary)] rounded-lg hover:text-[var(--primary)] hover:border-[var(--primary)] transition-colors text-sm"
              disabled={refreshingAll}
              title="Refresh all previews"
            >
              <FaSync className={`text-xs ${refreshingAll ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* Links Display */}
      <div className="flex-1 overflow-auto">
        {data.links.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-[var(--text-secondary)]">
            <FaLink className="text-3xl mb-3 text-[var(--text-secondary)]" />
            <p className="text-base mb-1">No links added yet</p>
            <p className="text-sm">Add a link to see it displayed here</p>
          </div>
        ) : (
          <div className={data.links.length === 1 ? "space-y-0" : "space-y-2"}>
            {data.links.map(link => {
              const isLoading = loadingPreviews[link.id];
              const preview = link.preview;
              const isSingleLink = data.links.length === 1;
              
              if (isSingleLink) {
                // Single link - large card layout
                return (
                  <div key={link.id} className="border border-[var(--border-color)] rounded-xl bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)] transition-colors overflow-hidden">
                    {/* Large preview image at top */}
                    {preview?.image && (
                      <div className="w-full h-32 overflow-hidden">
                        <img 
                          src={preview.image} 
                          alt="Link preview" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.parentNode.style.display = 'none';
                          }}
                          onLoad={(e) => {
                            // Fade in effect when image loads
                            e.target.style.opacity = '0';
                            e.target.style.transition = 'opacity 0.3s ease-in-out';
                            setTimeout(() => {
                              e.target.style.opacity = '1';
                            }, 50);
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Content section */}
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        {/* Favicon */}
                        <div className="flex-shrink-0 w-5 h-5 mt-1">
                          {preview?.favicon ? (
                            <img 
                              src={preview.favicon} 
                              alt="Site icon" 
                              className="w-5 h-5 rounded"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : null}
                        </div>
                        
                        {/* Text content */}
                        <div className="flex-1 min-w-0">
                          {/* Title */}
                          {preview?.title && preview.title !== link.url ? (
                            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-1 leading-tight">
                              {preview.title}
                            </h3>
                          ) : null}
                          
                          {/* Domain */}
                          <a 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-[var(--primary)] hover:underline block mb-2"
                            title={link.url}
                          >
                            {preview?.domain || link.url}
                          </a>
                          
                          {/* Description */}
                          {preview?.description && (
                            <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-3">
                              {preview.description}
                            </p>
                          )}
                          
                          {/* Loading state */}
                          {(isLoading || preview?.loading) && (
                            <div className="flex items-center text-[var(--text-secondary)] text-sm mb-3">
                              <FaSpinner className="animate-spin mr-3 w-4 h-4" />
                              <span>Loading preview...</span>
                            </div>
                          )}
                          
                          {/* Error state */}
                          {preview?.error && !preview.loading && !isLoading && (
                            <div className="text-red-500 text-sm mb-3">
                              Could not load preview
                            </div>
                          )}
                          
                          {/* Action buttons */}
                          <div className="flex items-center gap-2">
                            <a 
                              href={link.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 px-3 py-1.5 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
                            >
                              <FaExternalLinkAlt className="w-3 h-3" />
                              Open Link
                            </a>
                            <button
                              onClick={() => removeLink(link.id)}
                              className="flex items-center gap-2 px-3 py-1.5 border border-[var(--border-color)] text-[var(--text-secondary)] rounded-lg hover:text-red-500 hover:border-red-500 transition-colors text-sm"
                            >
                              <FaTimes className="w-3 h-3" />
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              
              // Multiple links - compact layout
              return (
                <div key={link.id} className="border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)] transition-colors">
                  <div className="p-3">
                    {/* Main content area */}
                    <div className="flex items-start gap-3">
                      {/* Favicon */}
                      <div className="flex-shrink-0 w-4 h-4 mt-1">
                        {preview?.favicon ? (
                          <img 
                            src={preview.favicon} 
                            alt="Site icon" 
                            className="w-4 h-4 rounded"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : null}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3">
                          {/* Text content */}
                          <div className="flex-1 min-w-0">
                            {/* Title and domain */}
                            <div className="flex items-start justify-between mb-1">
                              <div className="flex-1 min-w-0">
                                {preview?.title && preview.title !== link.url ? (
                                  <h4 className="text-sm font-medium text-[var(--text-primary)] truncate mb-1">
                                    {preview.title}
                                  </h4>
                                ) : null}
                                <a 
                                  href={link.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-[var(--primary)] hover:underline block truncate"
                                  title={link.url}
                                >
                                  {preview?.domain || link.url}
                                </a>
                              </div>
                              
                              {/* Action buttons */}
                              <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                                <a 
                                  href={link.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="p-1 text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors"
                                  title="Open link"
                                >
                                  <FaExternalLinkAlt className="w-3 h-3" />
                                </a>
                                <button
                                  onClick={() => removeLink(link.id)}
                                  className="p-1 text-[var(--text-secondary)] hover:text-red-500 transition-colors"
                                  title="Remove link"
                                >
                                  <FaTimes className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Description */}
                            {preview?.description && (
                              <p className="text-xs text-[var(--text-secondary)] line-clamp-2">
                                {preview.description}
                              </p>
                            )}
                            
                            {/* Loading state */}
                            {(isLoading || preview?.loading) && (
                              <div className="flex items-center text-[var(--text-secondary)] text-xs mt-1">
                                <FaSpinner className="animate-spin mr-2 w-3 h-3" />
                                <span>Loading preview...</span>
                              </div>
                            )}
                            
                            {/* Error state */}
                            {preview?.error && !preview.loading && !isLoading && (
                              <div className="text-red-500 text-xs mt-1">
                                Could not load preview
                              </div>
                            )}
                          </div>
                          
                          {/* Preview image - on the right */}
                          {preview?.image && (
                            <div className="flex-shrink-0">
                              <img 
                                src={preview.image} 
                                alt="Link preview" 
                                className="w-24 h-16 object-cover rounded border border-[var(--border-color)]"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                                onLoad={(e) => {
                                  // Fade in effect when image loads
                                  e.target.style.opacity = '0';
                                  e.target.style.transition = 'opacity 0.3s ease-in-out';
                                  setTimeout(() => {
                                    e.target.style.opacity = '1';
                                  }, 50);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
